import { useState } from 'preact/hooks';
import type { Dispatch, StateUpdater } from 'preact/hooks';
import { checkIn, cancelEvent, type MyEventsResponse, type MyEvent } from '../lib/api';
import { EventDetailModal } from '../components/EventDetailModal';
import { EventChat } from './EventChat';
import { MiReporte } from './MiReporte';
import { CerrarEvento } from './CerrarEvento';
import { ResultadosEvento } from './ResultadosEvento';
import { CalificarEquipo } from './CalificarEquipo';
import { Briefing } from './Briefing';
import { SwapPicker } from './SwapPicker';
import { eventHasStarted, eventDatePassed, isLateCancel } from '../lib/dates';
import { haptic } from '../lib/telegram';

interface UseEventFlowsOpts {
  data: MyEventsResponse | null;
  setData: Dispatch<StateUpdater<MyEventsResponse | null>>;
  /** Screen-specific refresh, called after a mutation (cancel, report, closure, swap). */
  reload: () => void | Promise<void>;
}

/**
 * Shared orchestration for the event detail modal and its sub-screens
 * (chat, briefing, reporte, cierre, resultados, calificación, swap).
 * Used by BOTH the Eventos tab and Inicio — the full action set must stay
 * identical between them, so any new flow lands here, never in one screen.
 *
 * The caller keeps ownership of data loading (Eventos is cache-first,
 * Inicio is plain fetch); this hook owns everything from "a card was
 * tapped" onward. Render `flows` at the end of the screen's JSX.
 */
export function useEventFlows({ data, setData, reload }: UseEventFlowsOpts) {
  const [selected, setSelected] = useState<MyEvent | null>(null);
  const [chatEvent, setChatEvent] = useState<MyEvent | null>(null);
  const [reporteEvent, setReporteEvent] = useState<MyEvent | null>(null);
  const [cerrarEvent, setCerrarEvent] = useState<MyEvent | null>(null);
  const [resultadosEvent, setResultadosEvent] = useState<MyEvent | null>(null);
  const [calificarEvent, setCalificarEvent] = useState<MyEvent | null>(null);
  const [briefingEvent, setBriefingEvent] = useState<MyEvent | null>(null);
  const [swapEvent, setSwapEvent] = useState<MyEvent | null>(null);
  const [busy, setBusy] = useState(false);

  async function checkInEvent(ev: MyEvent) {
    if (busy) return;
    setBusy(true);
    haptic('success');
    // Optimistic — update UI before the request resolves. The event can live in
    // `today` OR `pendingClosure` (a late check-in on an unclosed past event), so
    // flip both buckets — otherwise the pendingClosure card keeps checkedIn=false
    // and reopening it hides "Mi reporte" until a reload.
    const flip = (v: boolean) => (prev: MyEventsResponse | null) => prev ? {
      ...prev,
      today: prev.today.map((e) => e.id === ev.id ? { ...e, checkedIn: v } : e),
      pendingClosure: (prev.pendingClosure || []).map((e) => e.id === ev.id ? { ...e, checkedIn: v } : e),
    } : prev;
    setData(flip(true));
    setSelected((prev) => prev && prev.id === ev.id ? { ...prev, checkedIn: true } : prev);
    try {
      const r = await checkIn(ev.id);
      setSelected((prev) => prev && prev.id === ev.id ? null : prev);
      if (r.queued) {
        // Offline — checkIn() captured the user-tap timestamp and queued the
        // write. The queue drains on next 'online' or app reopen, replaying
        // with the saved clientTimestamp so arrival time stays accurate.
        haptic('warning');
        alert('✓ Check-in guardado. Se sincronizará automáticamente cuando vuelvas a tener conexión.');
      }
    } catch (e) {
      setData(flip(false));
      setSelected((prev) => prev && prev.id === ev.id ? { ...prev, checkedIn: false } : prev);
      haptic('error');
      alert(`No pudimos hacer check-in: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    if (!selected || busy) return;
    // Warn exactly when the server will charge (noon-AST anchor, < 24h) — not
    // just "is today": the night before is already late on the server.
    const isLate = isLateCancel(selected);
    const warning = isLate
      ? `⚠️ Cancelar a menos de 24h aplica una penalidad de RD$ 2,500 + strike.\n\n¿Estás seguro? Si es emergencia médica/seguridad, llama a Angie primero.`
      : `Tu cupo se ofrecerá a otro miembro del equipo.\n\n¿Estás seguro?`;
    if (!confirm(warning)) return;
    const reason = prompt('Motivo (opcional):') || '';

    setBusy(true);
    haptic('warning');
    try {
      const r = await cancelEvent(selected.id, reason);
      haptic(r.isLate ? 'warning' : 'success');
      setSelected(null);
      await reload();
      if (r.penalty) {
        alert(`Cancelado. Penalidad de RD$ ${r.penalty.monto.toLocaleString('es-DO')} aplicada.`);
      } else {
        alert('Cancelado. Tu cupo está abierto al equipo.');
      }
    } catch (e) {
      haptic('error');
      const msg = (e as Error).message;
      const known: Record<string, string> = {
        already_cancelled: 'Este evento ya estaba cancelado.',
        event_past: 'Este evento ya pasó — ya no se puede cancelar desde la app. Si hubo un problema ese día, habla con Angie o Wendy.',
        event_closed: 'Este evento ya está en cierre o cerrado — habla con Angie o Wendy.',
        event_postponed: 'Este evento está pospuesto — Operaciones avisará la nueva fecha; tu asignación quedó en espera. Si no vas a poder, habla con Angie o Wendy.',
        event_cancelled: 'Este evento fue cancelado — no tienes que hacer nada.',
        event_not_found: 'No encontramos el evento — avisa a Operaciones.',
      };
      alert(known[msg] || `No pudimos cancelar: ${msg}`);
      // Every terminal refusal means the card is stale — refresh so the dead
      // button does not linger (mirrors Ofertas.tsx).
      if (known[msg]) await reload();
    } finally {
      setBusy(false);
    }
  }

  const today = data?.today || [];
  const pendingClosure = data?.pendingClosure || [];

  // Two clocks, both mirrored from the API (which is the authority):
  //  · Cambiar turno — before the CALL TIME (`startMs`).
  //  · No puedo ir — until the event DAY is over and while the event is not
  //    in closure (`canCancel`, computed server-side on the authoritative
  //    event row): a late cancel on the day must still run (penalty, Ops
  //    alert, replacement search). A past event in "Pendiente de cierre" used
  //    to keep both buttons (EV-0703, 2026-09-14).
  // No bucket precondition: buckets come from the row's denormalized date,
  // which can be stale; the authoritative fields decide, and an event the API
  // could not resolve (`startMs === null` / `canCancel === false`) gets
  // neither — the API would answer 409/404 anyway; this just hides dead ends.
  function actionable(ev: MyEvent): boolean {
    return ev.status !== 'Completado' && !ev.status.startsWith('Cancelado');
  }
  function canSwap(ev: MyEvent): boolean {
    if (!actionable(ev)) return false;
    return typeof ev.canSwap === 'boolean' ? ev.canSwap : !eventHasStarted(ev);
  }
  // Prefer the server verdict; the date-only fallback exists for payloads
  // cached before the field shipped.
  function canCancel(ev: MyEvent): boolean {
    if (!actionable(ev)) return false;
    return typeof ev.canCancel === 'boolean' ? ev.canCancel : !eventDatePassed(ev);
  }

  const flows = (
    <>
      {selected && (
        <EventDetailModal
          event={selected}
          actionable={today.some((e) => e.id === selected.id) || pendingClosure.some((e) => e.id === selected.id)}
          onClose={() => setSelected(null)}
          onCheckIn={selected.status !== 'Completado' && !selected.checkedIn ? () => checkInEvent(selected) : undefined}
          onCancel={canCancel(selected) ? handleCancel : undefined}
          onSwap={canSwap(selected) ? () => { setSwapEvent(selected); setSelected(null); } : undefined}
          onChat={() => { setChatEvent(selected); setSelected(null); }}
          onMiReporte={() => { setReporteEvent(selected); setSelected(null); }}
          onCerrar={() => { setCerrarEvent(selected); setSelected(null); }}
          onResultados={selected.status === 'Completado' ? () => { setCalificarEvent(selected); setSelected(null); } : undefined}
          onBriefing={() => { setBriefingEvent(selected); setSelected(null); }}
          busy={busy}
        />
      )}

      {chatEvent && (
        <EventChat
          eventId={chatEvent.id}
          eventTitle={chatEvent.title}
          onClose={() => setChatEvent(null)}
        />
      )}

      {reporteEvent && (
        <MiReporte
          eventId={reporteEvent.id}
          eventTitle={reporteEvent.title}
          onClose={() => { setReporteEvent(null); reload(); }}
        />
      )}

      {cerrarEvent && (
        <CerrarEvento
          eventId={cerrarEvent.id}
          eventTitle={cerrarEvent.title}
          onClose={() => { setCerrarEvent(null); reload(); }}
          onShowResults={() => { const ev = cerrarEvent; setCerrarEvent(null); setCalificarEvent(ev); }}
        />
      )}

      {calificarEvent && (
        <CalificarEquipo
          eventId={calificarEvent.id}
          eventTitle={calificarEvent.title}
          onDone={() => { const ev = calificarEvent; setCalificarEvent(null); setResultadosEvent(ev); }}
        />
      )}

      {resultadosEvent && (
        <ResultadosEvento
          eventId={resultadosEvent.id}
          eventTitle={resultadosEvent.title}
          onClose={() => { setResultadosEvent(null); reload(); }}
        />
      )}

      {briefingEvent && (
        <Briefing
          eventId={briefingEvent.id}
          eventTitle={briefingEvent.title}
          onClose={() => setBriefingEvent(null)}
        />
      )}

      {swapEvent && (
        <SwapPicker
          eventId={swapEvent.id}
          eventTitle={swapEvent.title}
          onClose={() => { setSwapEvent(null); reload(); }}
        />
      )}
    </>
  );

  return { selected, setSelected, busy, checkInEvent, openChat: setChatEvent, flows };
}
